import React, { Fragment, useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { object, func, bool, string } from 'prop-types';
import cn from 'classnames';
import ActionableHeaderCard from 'ps-components/Card/ActionableHeaderCard';
import { SLIDEOUTS } from '../../../Slideout/Slideouts';
import SlideoutBody from 'ps-components/Slideout/Body/SlideoutBody';
import SlideoutFooter from 'ps-components/Slideout/Footer/SlideoutFooter';
import SlideoutHeaderButton from 'ps-components/Slideout/Header/SlideoutHeaderButton';
import Pagination from 'ps-components/Pagination/Pagination';
import Popover from 'ps-components/Popover/Popover';
import ListPanel from 'ps-components/AdvancedTable/ListPanel/ListPanel';
import Search from 'ps-components/Search/Search';
import { Toggle } from 'ps-design';
import InPlaceIndicator from 'ps-components/LoadingIndicator/InPlaceIndicator/InPlaceIndicator';
import SlideoutFooterStandardButtons from 'ps-components/Slideout/Footer/SlideoutFooterStandardButtons';
import Placeholder from 'ps-components/Placeholder/Placeholder';
import * as marketplaceMatchesActions from '../MarketplaceMatchesActions';
import { formatCurrency } from 'ps-components/Utils/currencyUtils';
import moment from 'moment';

import './MarketplaceMatchesSlideout.scss';

const SURVEY_DATA_TITLE = 'Survey Data';
const SURVEY_SCHEDULE_TITLE = 'Survey Schedule';
const SURVEY_PARTICIPANTS_TITLE = 'Survey Participants';
const SURVEY_JOBS_TITLE = 'Survey Jobs';
const RETURN_TO_ALL = 'Return to all suggested matches';
const LIMIT = 10;
const PAGE_RANGE = 2;

SurveyDetailsSlideout.propTypes = {
  surveyDetails: object,
  showSlideout: func,
  hideSlideout: func,
  actions: {
    getSurveyDetails: func,
    purchaseSubmit: func,
    resetPurchaseSubmit: func
  },
  onBack: func,
  isBigger: bool,
  purchaseSubmit: {
    loading: bool,
    success: bool,
    error: string
  },
  readOnly: bool
};

function SurveyDetailsSlideout(props) {
  const [offset, setOffset] = useState(0);
  const [jobOffset, setJobOffset] = useState(0);
  const [searchText, setSearchText] = useState('');
  const [jobSearchText, setJobSearchText] = useState('');
  const [selectedSurveyJob, setSelectedSurveyJob] = useState({});
  const [showPopover, setShowPopover] = useState(false);
  const [showParticipationPrice, setShowParticipationPrice] = useState(false);

  const { surveyDetails, isBigger, readOnly } = props;
  const details = surveyDetails?.details;
  const surveyJobs = surveyDetails?.surveyJobs;
  const filteredSurveyJobs = surveyDetails?.filteredSurveyJobs;
  const isPurchasable =
    details?.available_for_purchase &&
    (details?.purchase_price != null || details?.participation_purchase_price != null) &&
    details?.contract_language_url;

  const disabledPurchaseText = readOnly
    ? 'Not available for read-only users'
    : 'This survey is currently not available to purchase through MarketPay';

  useEffect(() => {
    setJobSearchText('');
    setJobOffset(0);
    setSearchText('');
    setOffset(0);
    setSelectedSurveyJob({});
  }, [details?.cut_id]);

  // Checks for no details found
  const showNoDetailsPlaceholder = !surveyDetails?.details?.loading && (!details || Object.keys(details).length === 0);

  const noDetailsPlaceholder = (
    <div
      id="marketplace-matches__details-no-results-container"
      className="marketplace-matches__details-no-results-container"
    >
      <Placeholder
        className="pxl-text-small pxl-text-muted"
        imagePath="/view/static/mp/images/empty_results.svg"
        primaryText="No Survey Details Found"
        secondaryText="Please try again later or contact support for more information"
        showText={true}
      />
    </div>
  );

  // Pagination logic for participants list
  function pageParticipants(limit, offset) {
    setOffset(offset);
  }

  // Pagination logic for survey jobs list
  function pageSurveyJobs(limit, offset) {
    setJobOffset(offset);
  }

  function formatDate(date) {
    return moment(date).format('YYYY-MM-DD');
  }

  function getDataRow(el, index) {
    return <div className="participant-row">{el.participant}</div>;
  }

  function togglePopover() {
    setShowPopover(false);
  }

  function getDataRowSurveyJob(el, index) {
    const jobDescriptionLinkRef = React.createRef();
    const rowId = `survey-job-row-${index}`;

    return (
      <Fragment>
        <div
          id={rowId}
          ref={jobDescriptionLinkRef}
          className="survey-job-row"
          onClick={() => {
            setShowPopover(true);
            setSelectedSurveyJob(el);
          }}
        >
          {el.job_title}
        </div>
        <div id={`${rowId}-anchor`} className="survey-job-row_anchor" />

        {selectedSurveyJob.job_title === el.job_title &&
          showPopover &&
          ReactDOM.createPortal(
            <Popover
              triggerElement={jobDescriptionLinkRef}
              relativeElement={document.querySelector(rowId)}
              popoverText={el.job_description}
              togglePopover={togglePopover}
              display={showPopover}
            />,
            document.getElementById(`${rowId}-anchor`)
          )}
      </Fragment>
    );
  }

  // track text input change on every change
  function searchOnChange(e) {
    setSearchText(e?.target?.value);
    setOffset(0);
  }

  // clicking x to clear search text
  function searchOnClick() {
    setSearchText('');
    setOffset(0);
  }

  // when pressing enter to execute a search
  function searchOnKeyDown(event) {
    if (event.key === 'Enter') {
      setSearchText(searchText);
      setOffset(0);
    }
  }

  // clicking the search icon to execute a search
  function searchOnSearch() {
    setSearchText(searchText);
    setOffset(0);
  }

  // track text input change on every change
  function searchOnChangeJobs(e) {
    setJobSearchText(e?.target?.value);
    setJobOffset(0);
  }

  // clicking x to clear search text
  function searchOnClickJobs() {
    setJobSearchText('');
    setJobOffset(0);
  }

  // when pressing enter to execute a search
  function searchOnKeyDownJobs(event) {
    if (event.key === 'Enter') {
      setJobSearchText(searchText);
      setJobOffset(0);
    }
  }

  // clicking the search icon to execute a search
  function searchOnSearchJobs() {
    setJobSearchText(searchText);
    setJobOffset(0);
  }

  function getParticipants() {
    if (!details?.participants || details?.participants?.length === 0) {
      return <div>Participant List Not Available</div>;
    }

    const textParts =
      details?.participants.filter((p, index) => {
        if (
          searchText === '' ||
          p.participant
            ?.toLowerCase()
            .trim()
            .includes(searchText.toLowerCase().trim())
        ) {
          return p;
        }
      }) || [];

    const parts =
      textParts?.filter((p, index) => {
        if (index >= offset && index < offset + LIMIT) {
          return p;
        }
      }) || [];

    return (
      <div className="participants-container">
        <Search
          className="marketplace-matches__details-search"
          id="marketplace-matches__details-search"
          onChange={searchOnChange}
          onClick={searchOnClick}
          onKeyDown={searchOnKeyDown}
          onSearch={searchOnSearch}
          placeholder="search participants"
          testId="marketplace-matches__details-search"
          value={searchText}
        />
        <ListPanel
          className="marketplace-matches__participants-list"
          totalCount={textParts.length}
          columnHeaders={['']}
          dataLoading={false}
          getDataRow={getDataRow}
          isAllChecked={false}
          onSelectAll={null}
          rowClick={null}
          rowData={parts}
          stickyHeader={false}
          useSelectAll={false}
          rowIndexPrefix="participant-list-"
          rowClassName="participants-list__row"
        />
        <Pagination
          pageShowRange={PAGE_RANGE}
          offset={offset}
          pageAction={pageParticipants}
          step={LIMIT}
          totalCount={textParts?.length}
        />
      </div>
    );
  }

  function getAllSurveyJobs() {
    if (!details?.allSurveyJobs || details?.allSurveyJobs?.length === 0) {
      return <div>Survey Job List Not Available</div>;
    }

    const textSurveyJobs =
      details?.allSurveyJobs.filter((j, index) => {
        if (
          (j.job_title && jobSearchText === '') ||
          j.job_title
            ?.toLowerCase()
            .trim()
            .includes(jobSearchText.toLowerCase().trim())
        ) {
          return j;
        }
      }) || [];

    const surveyJobs =
      textSurveyJobs?.filter((j, index) => {
        if (index >= jobOffset && index < jobOffset + LIMIT) {
          return j;
        }
      }) || [];

    return (
      <div className="all-jobs-container">
        <Search
          className="marketplace-matches__details-search"
          id="marketplace-matches__details-search"
          onChange={searchOnChangeJobs}
          onClick={searchOnClickJobs}
          onKeyDown={searchOnKeyDownJobs}
          onSearch={searchOnSearchJobs}
          placeholder="search survey jobs"
          testId="marketplace-matches__details-search-jobs"
          value={jobSearchText}
        />
        <ListPanel
          className="marketplace-matches__survey-jobs-list"
          totalCount={surveyJobs.length}
          columnHeaders={['']}
          dataLoading={false}
          getDataRow={getDataRowSurveyJob}
          isAllChecked={false}
          onSelectAll={null}
          rowClick={null}
          rowData={surveyJobs}
          stickyHeader={false}
          useSelectAll={false}
          rowIndexPrefix="survey-jobs-list-"
          rowClassName="survey-jobs-list__row"
        />
        <Pagination
          pageShowRange={PAGE_RANGE}
          offset={jobOffset}
          pageAction={pageSurveyJobs}
          step={LIMIT}
          totalCount={textSurveyJobs?.length}
        />
      </div>
    );
  }

  function togglePurchasePrice() {
    setShowParticipationPrice(!showParticipationPrice);
  }

  function getPurchaseToggle() {
    const hasBothPrices = details?.purchase_price != null && details?.participation_purchase_price != null;

    if (isPurchasable && hasBothPrices) {
      return (
        <div className="purchase-price-toggle-container">
          <Toggle
            onChange={togglePurchasePrice}
            id="purchase-price-toggle"
            className="purchase-price-toggle"
            value={showParticipationPrice}
          />
          <span className="toggle-text">Show Participation Price</span>
        </div>
      );
    }

    return null;
  }

  function getPurchasePrice() {
    if (isPurchasable) {
      return (
        <Fragment>
          {details?.purchase_price && !showParticipationPrice && (
            <div className="survey-price">Survey Price: {formatCurrency(details?.purchase_price, 'USD')} USD</div>
          )}
          {details?.participation_purchase_price && showParticipationPrice && (
            <div className="survey-price">
              Survey Price: {formatCurrency(details?.participation_purchase_price, 'USD')} USD
            </div>
          )}
          {details?.participation_purchase_price && !details?.purchase_price && (
            <div className="survey-price">
              Survey Price (Participation Required): {formatCurrency(details?.participation_purchase_price, 'USD')} USD
            </div>
          )}
        </Fragment>
      );
    }

    return null;
  }

  function openPurchaseSlideout() {
    props.showSlideout(SLIDEOUTS.MPM_PURCHASE, {
      ...props,
      onBack: openDetailsSlideout,
      initToggle: showParticipationPrice
    });
  }

  function openDetailsSlideout() {
    props.showSlideout(SLIDEOUTS.MARKETPLACE_MATCHES_SURVEY_DETAILS, {
      ...props
    });
  }

  return (
    <div
      className={cn('marketplace-matches__settings-slideout', { bigger: isBigger })}
      id="marketplace-matches__details-slideout"
    >
      <div className="slideout-header">
        <div
          className="action-container previous"
          onClick={() => props.actions.getSurveyDetails(details, surveyJobs, filteredSurveyJobs, 'previous')}
          data-test-id="jbr-navigate-previous-job-button"
        >
          <div className="action-icon">
            <i className="icon-left-open" />
          </div>
          <div className="action-label">Previous Survey Job</div>
        </div>
        <div
          className="action-container next"
          data-test-id="jbr-navigate-next-job-button"
          onClick={() => props.actions.getSurveyDetails(details, surveyJobs, filteredSurveyJobs, 'next')}
        >
          <div className="action-label">Next Survey Job</div>
          <div className="action-icon">
            <i className="icon-right-open" />
          </div>
        </div>

        <SlideoutHeaderButton
          buttonClass="slideout-header__button"
          iconClass="icon-cancel"
          onClick={props.hideSlideout}
          testId="marketplace-matches__navigation-close"
        />
      </div>

      <SlideoutBody>
        {surveyDetails?.loading ? (
          <InPlaceIndicator />
        ) : (
          <React.Fragment>
            {props.onBack && (
              <div className="return-link" onClick={props.onBack}>
                <i className="icon-reply" />
                {RETURN_TO_ALL}
              </div>
            )}

            {showNoDetailsPlaceholder ? (
              noDetailsPlaceholder
            ) : (
              <React.Fragment>
                <ActionableHeaderCard
                  cardTitle={details?.job_title || ''}
                  className="marketplace-matches__card"
                  isCollapsible={true}
                >
                  <div className="marketplace-matches__details-row">
                    <div className="survey-code">{details?.job_code}</div>
                    {details?.confidence >= 0.3 && <div className="confidence">Frequently Matched</div>}
                  </div>
                  <div className="survey-title">{`${details?.pub_survey_short_name} (${details?.pubSurveyCode})`}</div>
                  <div className="description">{details?.job_description}</div>
                </ActionableHeaderCard>

                <ActionableHeaderCard
                  cardTitle={SURVEY_DATA_TITLE}
                  className="marketplace-matches__card"
                  isCollapsible={true}
                >
                  <div className="survey-data-column">
                    <div className="survey-data-group">
                      <div className="title">Effective Date</div>
                      <div className="value">
                        {details?.pub_survey_effective_date ? formatDate(details?.pub_survey_effective_date) : '-'}
                      </div>
                    </div>

                    <div className="survey-data-group">
                      <div className="title">Currency</div>
                      <div className="value">{details?.currency || '-'}</div>
                    </div>

                    <div className="survey-data-group">
                      <div className="title">Industry</div>
                      <div className="value">{details?.industry || '-'}</div>
                    </div>

                    <div className="survey-data-group">
                      <div className="title">Geography</div>
                      <div className="value">{details?.geography || '-'}</div>
                    </div>
                  </div>

                  <div className="survey-data-column">
                    <div className="survey-data-group">
                      <div className="title">Overall Companies Participated</div>
                      <div className="value">{details?.total_companies || '-'}</div>
                    </div>

                    <div className="survey-data-group">
                      <div className="title">Overall Jobs Surveyed</div>
                      <div className="value">{details?.total_jobs || '-'}</div>
                    </div>

                    <div className="survey-data-group">
                      <div className="title"># of Employees</div>
                      <div className="value">{details?.total_incumbents || '-'}</div>
                    </div>

                    <div className="survey-data-group">
                      <div className="title"># of Participants</div>
                      <div className="value">{details?.total_participants || '-'}</div>
                    </div>
                  </div>

                  <div className="survey-data-url-row">
                    <div className="title">Survey URL</div>
                    <a target="_blank" href={details?.more_info_url || '#'} className="value" rel="noopener noreferrer">
                      {details?.more_info_url || '-'}
                    </a>
                  </div>
                </ActionableHeaderCard>

                <ActionableHeaderCard
                  cardTitle={SURVEY_SCHEDULE_TITLE}
                  className="marketplace-matches__card"
                  isCollapsible={true}
                >
                  <div className="survey-data-column">
                    <div className="survey-data-group">
                      <div className="title">Participation Deadline</div>
                      <div className="value">
                        {details?.survey_due_date ? formatDate(details?.survey_due_date) : '-'}
                      </div>
                    </div>
                  </div>

                  <div className="survey-data-column">
                    <div className="survey-data-group">
                      <div className="title">Report Available</div>
                      <div className="value">
                        {details?.expected_publication_date ? formatDate(details?.expected_publication_date) : '-'}
                      </div>
                    </div>
                  </div>
                </ActionableHeaderCard>

                <ActionableHeaderCard
                  cardTitle={SURVEY_PARTICIPANTS_TITLE}
                  className="marketplace-matches__card search-card"
                  isCollapsible={true}
                >
                  <div>{getParticipants()}</div>
                </ActionableHeaderCard>

                <ActionableHeaderCard
                  cardTitle={SURVEY_JOBS_TITLE}
                  className="marketplace-matches__card search-card"
                  isCollapsible={true}
                >
                  <div>{getAllSurveyJobs()}</div>
                </ActionableHeaderCard>
              </React.Fragment>
            )}
          </React.Fragment>
        )}
      </SlideoutBody>

      <SlideoutFooter isBoxShadowTheme={true}>
        {getPurchaseToggle()}
        {getPurchasePrice()}

        {!surveyDetails?.loading && isPurchasable && (
          <SlideoutFooterStandardButtons
            showCancel={false}
            showApply={true}
            applyText="Purchase This Survey"
            doApply={openPurchaseSlideout}
            isApplyDisabled={readOnly || !isPurchasable}
            disabledText={disabledPurchaseText}
            applyClassName="purchase-this-survey-button"
          />
        )}
      </SlideoutFooter>
    </div>
  );
}

const mapStateToProps = ({ marketPricing }) => {
  return {
    ...marketPricing.MarketplaceMatchesReducer.marketplaceMatchesReducer
  };
};

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators(
    {
      ...marketplaceMatchesActions
    },
    dispatch
  )
});

export default connect(mapStateToProps, mapDispatchToProps)(SurveyDetailsSlideout);
